import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OrderList = ({ 
  orders, 
  selectedOrders, 
  onOrderSelect, 
  onSelectAll, 
  onStatusUpdate, 
  onRowClick,
  onSendNotification,
  isLoading 
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      processing: { label: 'Procesando', color: 'bg-info-100 text-info', icon: 'Clock' },
      packed: { label: 'Empaquetado', color: 'bg-warning-100 text-warning', icon: 'Package' },
      shipped: { label: 'Enviado', color: 'bg-primary-100 text-primary', icon: 'Send' },
      in_transit: { label: 'En tránsito', color: 'bg-secondary-100 text-secondary', icon: 'Truck' },
      delivered: { label: 'Entregado', color: 'bg-success-100 text-success', icon: 'CheckCircle' }
    };
    
    const config = statusConfig[status] || statusConfig.processing;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium ${config.color}`}>
        <Icon name={config.icon} size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { label: 'Alta', color: 'bg-error-100 text-error' },
      medium: { label: 'Media', color: 'bg-warning-100 text-warning' },
      low: { label: 'Baja', color: 'bg-success-100 text-success' }
    };
    
    const config = priorityConfig[priority] || priorityConfig.medium;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getNextStatusOptions = (currentStatus) => {
    const statusFlow = {
      processing: ['packed'],
      packed: ['shipped'],
      shipped: ['in_transit'],
      in_transit: ['delivered'],
      delivered: []
    };
    return statusFlow[currentStatus] || [];
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-8">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-text-muted">Cargando pedidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading-semibold text-text-primary">
            Lista de Pedidos
          </h2>
          <div className="text-sm text-text-muted">
            {orders.length} {orders.length === 1 ? 'pedido' : 'pedidos'}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedOrders.length === orders.length && orders.length > 0}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Pedido
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Cliente
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Entrega
              </th>
              <th className="px-4 py-3 text-right text-xs font-body-medium text-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {orders.map((order) => (
              <tr 
                key={order.id}
                className={`hover:bg-secondary-50 transition-micro cursor-pointer ${
                  selectedOrders.includes(order.id) ? 'bg-primary-50' : ''
                }`}
                onClick={() => onRowClick?.(order)}
              >
                <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => onOrderSelect(order.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-body-medium text-text-primary">
                      {order.id}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      {getPriorityBadge(order.priority)}
                      <span className="text-xs text-text-muted">
                        {formatCurrency(order.totalAmount)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-body-medium text-text-primary">
                      {order.customerName}
                    </div>
                    <div className="text-xs text-text-muted">
                      {order.products?.length} {order.products?.length === 1 ? 'producto' : 'productos'}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    {getStatusBadge(order.status)}
                    {order.trackingNumber && (
                      <div className="text-xs text-text-muted">
                        {order.carrier}: {order.trackingNumber}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-text-secondary">
                    {order.status === 'delivered' && order.actualDelivery 
                      ? formatDate(order.actualDelivery)
                      : formatDate(order.estimatedDelivery)
                    }
                  </div>
                  <div className="text-xs text-text-muted">
                    {order.status === 'delivered' ? 'Entregado' : 'Estimado'}
                  </div>
                </td>
                <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end space-x-2">
                    {getNextStatusOptions(order.status).map(nextStatus => (
                      <Button
                        key={nextStatus}
                        variant="outline"
                        size="sm"
                        onClick={() => onStatusUpdate(order.id, nextStatus)}
                        className="text-xs"
                      >
                        {nextStatus === 'packed' && 'Marcar empaquetado'}
                        {nextStatus === 'shipped' && 'Marcar enviado'}
                        {nextStatus === 'in_transit' && 'En tránsito'}
                        {nextStatus === 'delivered' && 'Confirmar entrega'}
                      </Button>
                    ))}
                    
                    <div className="relative group">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreVertical"
                        iconSize={16}
                      />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-modal border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-micro z-dropdown">
                        <div className="py-2">
                          <button
                            onClick={() => onSendNotification(order.id, 'status_update')}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                          >
                            <Icon name="Mail" size={16} />
                            <span>Enviar notificación</span>
                          </button>
                          <button
                            onClick={() => onSendNotification(order.id, 'tracking_info')}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-text-secondary hover:bg-secondary-50 hover:text-text-primary"
                          >
                            <Icon name="MapPin" size={16} />
                            <span>Info de seguimiento</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orders.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-body-medium text-text-primary mb-2">
            No hay pedidos
          </h3>
          <p className="text-text-muted">
            No se encontraron pedidos con los filtros aplicados
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderList;